import React, { useState } from "react";
import { useForm, Controller, useFormContext } from "react-hook-form";
import { useSnackbar } from "notistack";
import { Box, Button, Grid, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, RHFRadioGroup, RHFTextField } from "../../common/hook-form";
import { apiConstants, postGeneric } from "../../common/constants";
import PageTitle from "../../components/PageTitle";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";

import TuneIcon from "@mui/icons-material/Tune";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
    {
        label: "Order Configuration",
        desc: "Shipment type, courier & delivery options",
        icon: TuneIcon, tone: "blue",
        fields: ["order_type", "surface_Air", "appointmentDelivery", "mallDelivery", "csdDelivery", "carrierRisk"],
    },
    {
        label: "Order Info",
        desc: "Reference, invoice & insurance details",
        icon: DescriptionOutlinedIcon, tone: "teal",
        fields: ["reference", "invoice_number", "invoice_amount", "invoice_date"],
    },
    {
        label: "Pickup Details",
        desc: "Warehouse and pickup contact",
        icon: LocalShippingOutlinedIcon, tone: "orange",
        fields: ["pickup_warehouse_name", "pickup_person_name", "pickup_person_phone"],
    },
    {
        label: "Drop Details",
        desc: "Consignee address and delivery location",
        icon: PlaceOutlinedIcon, tone: "green",
        fields: ["drop_address", "drop_consignee", "drop_city", "drop_phone", "drop_pincode", "drop_region"],
    },
    {
        label: "Payment Details",
        desc: "Payment mode and COD configuration",
        icon: AccountBalanceWalletOutlinedIcon, tone: "purple",
        fields: ["mode_of_payment"],
    },
    {
        label: "Return Details",
        desc: "Return address (optional)",
        icon: ReplayOutlinedIcon, tone: "red",
        fields: [],
    },
    {
        label: "Packages",
        desc: "Dimensions, weight and package count",
        icon: Inventory2OutlinedIcon, tone: "teal",
        fields: ["order_ready_date", "package_total_weight", "total_package_count", "package1_no_of_units", "package1_length", "package1_width", "package1_height", "package1_weight"],
    },
    {
        label: "Review & Submit",
        desc: "Verify all details before submitting",
        icon: RateReviewOutlinedIcon, tone: "blue",
        fields: [],
    },
];

const TONE_MAP = {
    blue:   { bg: "#e3f2fd", color: "#1565c0" },
    teal:   { bg: "#e0f2f1", color: "#00695c" },
    green:  { bg: "#e8f5e9", color: "#2e7d32" },
    orange: { bg: "#fff4e5", color: "#ed6c02" },
    purple: { bg: "#f3e5f5", color: "#7b1fa2" },
    red:    { bg: "#ffebee", color: "#c62828" },
};

// ─── Styled components ────────────────────────────────────────────────────────

const WizardLayout = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2.5),
    alignItems: "flex-start",
}));

const StepperCard = styled(Paper)({
    width: 230,
    flexShrink: 0,
    borderRadius: 12,
    border: "1px solid #e0e0e0",
    boxShadow: "none",
    overflow: "hidden",
    position: "sticky",
    top: 80,
});

const StepperHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #f0f0f0",
    backgroundColor: "#fafafa",
}));

const StepItemRow = styled(Box)(({ theme, active, completed }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.1, 1.75),
    cursor: completed ? "pointer" : "default",
    backgroundColor: active ? "#eff6ff" : "transparent",
    borderLeft: active ? "3px solid #1565c0" : "3px solid transparent",
    transition: "background-color 120ms",
    "&:hover": completed && !active ? { backgroundColor: "#f5f5f5" } : {},
}));

const StepCircle = styled(Box)(({ status }) => ({
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: "11px",
    fontWeight: 700,
    marginTop: "1px",
    ...(status === "active" && { backgroundColor: "#1565c0", color: "#fff", boxShadow: "0 0 0 3px #dbeafe" }),
    ...(status === "completed" && { backgroundColor: "#dcfce7", color: "#15803d" }),
    ...(status === "pending" && { backgroundColor: "#f5f5f5", color: "#9e9e9e", border: "1.5px solid #e0e0e0" }),
}));

const StepConnectorLine = styled(Box)(({ completed }) => ({
    width: 2,
    height: 14,
    borderRadius: 2,
    margin: "2px 0 2px 11px",
    backgroundColor: completed ? "#86efac" : "#e0e0e0",
}));

const ContentArea = styled(Box)({ flex: 1, minWidth: 0 });

const FormCard = styled(Paper)({
    borderRadius: 12,
    border: "1px solid #e0e0e0",
    boxShadow: "none",
    overflow: "hidden",
});

const CardHead = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.25, 2.5),
    borderBottom: "1px solid #f0f0f0",
    backgroundColor: "#fafafa",
}));

const CardHeadTitle = styled(Typography)({
    fontWeight: 700,
    fontSize: "0.825rem",
    color: "#212121",
});

const CardBody = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 2.5),
}));

const StepDescBanner = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: "#eff6ff",
    borderBottom: "1px solid #dbeafe",
}));

const PkgBox = styled(Box)(({ theme }) => ({
    borderRadius: 8,
    border: "1px solid #e8e8e8",
    overflow: "hidden",
    "& + &": { marginTop: theme.spacing(1.5) },
}));

const PkgHead = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #e8e8e8",
}));

const PkgBadge = styled(Box)({
    fontSize: "11px",
    fontWeight: 700,
    color: "#4338ca",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    backgroundColor: "#ede9fe",
    border: "1px solid #c4b5fd",
    borderRadius: 4,
    padding: "2px 8px",
    lineHeight: 1.6,
});

const NavBar = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
}));

const NavBtn = styled(Button)(({ theme }) => ({
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.85rem",
    padding: theme.spacing(0.85, 2),
    boxShadow: "none",
    "&:hover": { boxShadow: "none" },
}));

const SubmitBtn = styled(LoadingButton)(({ theme }) => ({
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.85rem",
    padding: theme.spacing(0.85, 2.5),
    boxShadow: "none",
    "&:hover": { boxShadow: "none" },
}));

const ReviewCard = styled(Paper)({
    borderRadius: 10,
    border: "1px solid #e0e0e0",
    boxShadow: "none",
    overflow: "hidden",
    marginBottom: 12,
});

const ReviewField = styled(Box)({
    "& .rv-label": { fontSize: "11px", color: "#9e9e9e", marginBottom: 2 },
    "& .rv-value": { fontSize: "0.82rem", fontWeight: 600, color: "#212121" },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (v === true || v === "true") return "Yes";
    if (v === false || v === "false") return "No";
    return String(v);
};

function IconBox({ tone = "blue", children, size = 28 }) {
    const t = TONE_MAP[tone] || TONE_MAP.blue;
    return (
        <Box sx={{
            width: size, height: size, borderRadius: "7px",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            backgroundColor: t.bg, color: t.color,
        }}>
            {children}
        </Box>
    );
}

function FieldError({ error }) {
    if (!error) return null;
    return <Box sx={{ fontSize: "11px", color: "#dc2626", mt: "4px" }}>{error.message}</Box>;
}

// ─── Pill toggle group (replaces raw RadioGroup for step 0) ──────────────────

function FieldToggle({ name, label, options, externalChange }) {
    const { control, formState: { errors } } = useFormContext();
    const error = errors[name];
    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <Box>
                    <Typography sx={{
                        fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px",
                        textTransform: "uppercase", mb: 1,
                        color: error ? "#dc2626" : "#757575",
                    }}>
                        {label}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                        {options.map((opt) => {
                            const selected = String(field.value) === String(opt.value);
                            return (
                                <Box
                                    key={String(opt.value)}
                                    onClick={() => {
                                        field.onChange(opt.value);
                                        externalChange?.({ target: { value: opt.value } });
                                    }}
                                    sx={{
                                        px: 2, py: 0.75,
                                        borderRadius: "7px",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        cursor: "pointer",
                                        userSelect: "none",
                                        transition: "all 120ms ease",
                                        border: "1.5px solid",
                                        ...(selected
                                            ? { backgroundColor: "#1565c0", borderColor: "#1565c0", color: "#fff" }
                                            : { backgroundColor: "#fff", borderColor: "#e0e0e0", color: "#616161", "&:hover": { borderColor: "#1565c0", color: "#1565c0" } }
                                        ),
                                    }}
                                >
                                    {opt.label}
                                </Box>
                            );
                        })}
                    </Box>
                    {error && (
                        <Typography sx={{ fontSize: "11px", color: "#dc2626", mt: "4px" }}>
                            {error.message}
                        </Typography>
                    )}
                </Box>
            )}
        />
    );
}

// ─── Stepper panel ────────────────────────────────────────────────────────────

function StepperPanel({ currentStep, onStepClick }) {
    return (
        <StepperCard>
            <StepperHeader>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#9e9e9e", textTransform: "uppercase", letterSpacing: "0.7px" }}>
                    Steps
                </Typography>
            </StepperHeader>
            <Box sx={{ py: 1 }}>
                {STEPS.map((step, idx) => {
                    const status = idx < currentStep ? "completed" : idx === currentStep ? "active" : "pending";
                    const StepIcon = step.icon;
                    return (
                        <React.Fragment key={idx}>
                            <StepItemRow
                                active={status === "active" ? 1 : 0}
                                completed={status === "completed" ? 1 : 0}
                                onClick={() => status === "completed" && onStepClick(idx)}
                            >
                                <StepCircle status={status}>
                                    {status === "completed"
                                        ? <CheckCircleIcon sx={{ fontSize: 16, color: "#15803d" }} />
                                        : <StepIcon sx={{ fontSize: 13 }} />
                                    }
                                </StepCircle>
                                <Box>
                                    <Typography sx={{
                                        fontSize: "0.78rem",
                                        fontWeight: status === "active" ? 700 : 500,
                                        color: status === "pending" ? "#9e9e9e" : "#212121",
                                        lineHeight: 1.3,
                                    }}>
                                        {step.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: "10px", color: "#bdbdbd", mt: "2px", lineHeight: 1.3 }}>
                                        {step.desc}
                                    </Typography>
                                </Box>
                            </StepItemRow>
                            {idx < STEPS.length - 1 && (
                                <StepConnectorLine completed={idx < currentStep ? 1 : 0} />
                            )}
                        </React.Fragment>
                    );
                })}
            </Box>
        </StepperCard>
    );
}

// ─── Review step ──────────────────────────────────────────────────────────────

function ReviewStep({ values, parcels, onEdit }) {
    const sections = [
        {
            step: 0, title: "Order Configuration", icon: TuneIcon, tone: "blue",
            rows: [
                { label: "Order Type", value: fmt(values.order_type) },
                { label: "Select Courier", value: fmt(values.select_courier) },
                { label: "Shipment Type", value: fmt(values.surface_Air) },
                { label: "Appointment Required", value: fmt(values.appointmentDelivery) },
                { label: "Mall Delivery", value: fmt(values.mallDelivery) },
                { label: "CSD Delivery", value: fmt(values.csdDelivery) },
                { label: "Carrier Risk", value: fmt(values.carrierRisk) },
            ],
        },
        {
            step: 1, title: "Order Info", icon: DescriptionOutlinedIcon, tone: "teal",
            rows: [
                { label: "Reference", value: fmt(values.reference) },
                { label: "Invoice Number", value: fmt(values.invoice_number) },
                { label: "Invoice Amount", value: fmt(values.invoice_amount) },
                { label: "Eway Bill", value: fmt(values.ewayBill) },
                { label: "Invoice Date", value: fmt(values.invoice_date) },
                { label: "Insurance Taken", value: fmt(values.insurance_taken) },
            ],
        },
        {
            step: 2, title: "Pickup Details", icon: LocalShippingOutlinedIcon, tone: "orange",
            rows: [
                { label: "Warehouse Name", value: fmt(values.pickup_warehouse_name) },
                { label: "Person Name", value: fmt(values.pickup_person_name) },
                { label: "Person Phone", value: fmt(values.pickup_person_phone) },
                { label: "Pickup Reason", value: fmt(values.pickup_reason) },
            ],
        },
        {
            step: 3, title: "Drop Details", icon: PlaceOutlinedIcon, tone: "green",
            rows: [
                { label: "Address", value: fmt(values.drop_address) },
                { label: "Consignee", value: fmt(values.drop_consignee) },
                { label: "City", value: fmt(values.drop_city) },
                { label: "Phone", value: fmt(values.drop_phone) },
                { label: "Pincode", value: fmt(values.drop_pincode) },
                { label: "Region", value: fmt(values.drop_region) },
                { label: "GST Number", value: fmt(values.drop_gst_number) },
            ],
        },
        {
            step: 4, title: "Payment Details", icon: AccountBalanceWalletOutlinedIcon, tone: "purple",
            rows: [
                { label: "Payment Mode", value: fmt(values.mode_of_payment) },
                { label: "COD Amount", value: fmt(values.cod_amount) },
                { label: "COD Collection", value: fmt(values.cod_collection) },
                { label: "COD Type", value: fmt(values.codType) },
                { label: "Cheque In Favor Of", value: fmt(values.cheque_in_favor_of) },
            ],
        },
        {
            step: 5, title: "Return Details", icon: ReplayOutlinedIcon, tone: "red",
            rows: [
                { label: "Address", value: fmt(values.return_address) },
                { label: "Consignee", value: fmt(values.return_consignee) },
                { label: "City", value: fmt(values.return_city) },
                { label: "Phone", value: fmt(values.return_phone) },
                { label: "Pincode", value: fmt(values.return_pincode) },
                { label: "Region", value: fmt(values.return_region) },
            ],
        },
        {
            step: 6, title: "Packages & Dimensions", icon: Inventory2OutlinedIcon, tone: "teal",
            rows: [
                { label: "Order Ready Date", value: fmt(values.order_ready_date) },
                { label: "Total Weight (kg)", value: fmt(values.package_total_weight) },
                { label: "Total Package Count", value: fmt(values.total_package_count) },
                { label: "Pkg 1 Units", value: fmt(values.package1_no_of_units) },
                { label: "Pkg 1 L×W×H (cm)", value: `${fmt(values.package1_length)} × ${fmt(values.package1_width)} × ${fmt(values.package1_height)}` },
                { label: "Pkg 1 Weight (kg)", value: fmt(values.package1_weight) },
                ...parcels.map((_, i) => ([
                    { label: `Pkg ${i + 2} Units`, value: fmt(values[`package${i + 2}_no_of_units`]) },
                    { label: `Pkg ${i + 2} L×W×H (cm)`, value: `${fmt(values[`package${i + 2}_length`])} × ${fmt(values[`package${i + 2}_width`])} × ${fmt(values[`package${i + 2}_height`])}` },
                    { label: `Pkg ${i + 2} Weight (kg)`, value: fmt(values[`package${i + 2}_weight`]) },
                ])).flat(),
            ],
        },
    ];

    return (
        <Box>
            {sections.map((sec) => {
                const SecIcon = sec.icon;
                return (
                    <ReviewCard key={sec.step}>
                        <CardHead>
                            <IconBox tone={sec.tone} size={26}>
                                <SecIcon sx={{ fontSize: 14 }} />
                            </IconBox>
                            <CardHeadTitle>{sec.title}</CardHeadTitle>
                            <Tooltip title={`Edit ${sec.title}`}>
                                <IconButton
                                    size="small"
                                    onClick={() => onEdit(sec.step)}
                                    sx={{ ml: "auto", color: "#1565c0" }}
                                >
                                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                            </Tooltip>
                        </CardHead>
                        <CardBody sx={{ py: 1.5 }}>
                            <Grid container spacing={1.5}>
                                {sec.rows.map((row) => (
                                    <Grid item xs={12} sm={6} md={4} key={row.label}>
                                        <ReviewField>
                                            <div className="rv-label">{row.label}</div>
                                            <div className="rv-value">{row.value}</div>
                                        </ReviewField>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardBody>
                    </ReviewCard>
                );
            })}
        </Box>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CreatePickupRequest() {
    const [currentStep, setCurrentStep] = useState(0);
    const [paymentOption, setPaymentOption] = useState();
    const [autoallocate, setAutoallocate] = useState();
    const { enqueueSnackbar } = useSnackbar();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const initialParcelValues = { no_of_units: "", length: "", width: "", height: "", weight: "" };
    const [parcels, setParcels] = useState([]);

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                invoice_amount: Yup.string().required("Please write Invoice Amount"),
                invoice_number: Yup.string().required("Please write Invoice Number"),
                order_type: Yup.string().required("Please Select Order Type"),
                carrierRisk: Yup.string().required("Please Select Carrier Risk"),
                csdDelivery: Yup.string().required("Please Select CSD"),
                appointmentDelivery: Yup.string().required("Please Select Appointment Delivery"),
                mallDelivery: Yup.string().required("Please Select Mall Delivery"),
                invoice_date: Yup.string().required("Please select Invoice Date"),
                surface_Air: Yup.string().required("Please Select Mode"),
                pickup_warehouse_name: Yup.string().required("Please provide Pickup Warehouse Name"),
                pickup_person_name: Yup.string().required("Please write Pickup Person's Name"),
                pickup_person_phone: Yup.string().required("Please write Pickup Person's Phone"),
                drop_address: Yup.string().required("Please write Drop Address"),
                drop_consignee: Yup.string().required("Please write Drop Consignee"),
                drop_city: Yup.string().required("Please write Drop City"),
                drop_phone: Yup.string().required("Please write Drop Phone"),
                drop_pincode: Yup.string().required("Please write Drop Pincode"),
                drop_region: Yup.string().required("Please write Drop Region"),
                package_total_weight: Yup.string().required("Please write Package Total Weight"),
                total_package_count: Yup.string().required("Please write Total Package Count"),
                package1_no_of_units: Yup.string().required("Please write Package 1 Units"),
                package1_length: Yup.string().required("Please write Package 1 Length"),
                package1_width: Yup.string().required("Please write Package 1 Width"),
                package1_height: Yup.string().required("Please write Package 1 Height"),
                package1_weight: Yup.string().required("Please write Package 1 Weight"),
                mode_of_payment: Yup.string().required("Please write Mode Of Payment"),
                order_ready_date: Yup.string().required("Please write Order Ready Date"),
                reference: Yup.string().required("Please write Reference Id"),
            })
        ),
        defaultValues: {
            reference: "", invoice_amount: "", invoice_date: "", surface_Air: "",
            pickup_warehouse_name: "", pickup_person_name: "", pickup_person_phone: "",
            pickup_reason: "", drop_address: "", drop_consignee: "", drop_city: "",
            drop_phone: "", drop_pincode: "", drop_region: "", package_total_weight: "",
            total_package_count: "", package1_no_of_units: "", package1_length: "",
            package1_height: "", package1_width: "", package1_weight: "",
            package2_no_of_units: "", package2_length: "", package2_weight: "",
            package2_width: "", package2_height: "", return_address: "", return_city: "",
            return_consignee: "", return_phone: "", return_pincode: "", return_region: "",
            mode_of_payment: "", cod_amount: "", cod_collection: "", drop_gst_number: "",
            insurance_taken: "", order_ready_date: "", cheque_in_favor_of: "", codType: "",
            order_type: "", csdDelivery: "", appointmentDelivery: "", mallDelivery: "",
            carrierRisk: "", select_courier: "",
        },
    });

    const { handleSubmit, reset, watch, trigger, formState: { errors } } = methods;
    const allValues = watch();

    const handleNext = async () => {
        const stepFields = STEPS[currentStep].fields;
        const valid = stepFields.length > 0 ? await trigger(stepFields) : true;
        if (valid) setCurrentStep((s) => s + 1);
    };

    const handleBack = () => setCurrentStep((s) => s - 1);

    const onSubmit = (data) => {
        setIsSubmitting(true);
        let payload = {
            reference: data.reference,
            warehouseName: data.pickup_warehouse_name,
            dropoffLocation: {
                address: data.drop_address, city: data.drop_city, consignee: data.drop_consignee,
                phone: data.drop_phone, region: data.drop_region, zip: data.drop_pincode,
            },
            returnAddress: {
                address: data.return_address, city: data.return_city, consignee: data.return_consignee,
                phone: data.return_phone, region: data.return_pincode, zip: data.return_region,
            },
            orderReadyDate: data.order_ready_date,
            invoices: [{ invoiceNumber: data.invoice_number, invoiceAmount: data.invoice_amount, ewayBill: data.ewayBill }],
            orderType: data.order_type,
            paymentType: data.mode_of_payment,
            dispatchMode: data.surface_Air,
            insuranceRequired: data.insurance_taken,
            appointmentDelivery: data.appointmentDelivery,
            mallDelivery: data.mallDelivery,
            csdDelivery: data.csdDelivery,
            carrierRisk: data.carrierRisk,
            codType: data.codType ? data.codType : "CASH",
            isAutoAllocateWhileUpload: autoallocate === "autoAllocate" ? true : false,
            dimensions: [],
        };
        if (data.package1_no_of_units) {
            payload.dimensions.push({
                count: data.package1_no_of_units, height: data.package1_height,
                weight: data.package1_weight, width: data.package1_width, length: data.package1_length,
            });
        }
        parcels.forEach((parcel, index) => {
            if (data[`package${index + 2}_no_of_units`]) {
                payload.dimensions.push({
                    count: data[`package${index + 2}_no_of_units`],
                    height: data[`package${index + 2}_height`],
                    weight: data[`package${index + 2}_weight`],
                    width: data[`package${index + 2}_width`],
                    length: data[`package${index + 2}_length`],
                });
            }
        });
        postGeneric(apiConstants.CREATE_PICKUP_REQUEST, payload)
            .then((response) => {
                if (response.status === 200) {
                    const message = response?.data?.message || "Pickup request created";
                    enqueueSnackbar(message, { variant: "success" });
                    navigate("/ptl-pickup-requests");
                }
            })
            .catch((err) => {
                const message = err?.response?.data?.message || "Pickup request failed";
                enqueueSnackbar(message, { variant: "error" });
            })
            .finally(() => { setIsSubmitting(false); });
    };

    const addParcel = () => setParcels([...parcels, { id: parcels.length + 1, ...initialParcelValues }]);
    const removeParcel = (id) => setParcels(parcels.filter((p) => p.id !== id));

    const step = STEPS[currentStep];
    const StepIcon = step.icon;
    const isReview = currentStep === STEPS.length - 1;

    return (
        <>
            <PageTitle>Create Detailed Pickup Request</PageTitle>

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <WizardLayout>

                    {/* ── Left: Vertical stepper ─────────────────────────── */}
                    <StepperPanel currentStep={currentStep} onStepClick={setCurrentStep} />

                    {/* ── Right: Step content ────────────────────────────── */}
                    <ContentArea>

                        {/* Step header card */}
                        <FormCard sx={{ mb: 2 }}>
                            <StepDescBanner>
                                <IconBox tone={step.tone} size={24}>
                                    <StepIcon sx={{ fontSize: 13 }} />
                                </IconBox>
                                <Box>
                                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#1565c0" }}>
                                        Step {currentStep + 1} of {STEPS.length} — {step.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>
                                        {step.desc}
                                    </Typography>
                                </Box>
                            </StepDescBanner>
                        </FormCard>

                        {/* ── Step 0: Order Configuration ─────────────────── */}
                        {currentStep === 0 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                                {/* Shipment Setup */}
                                <FormCard>
                                    <CardHead>
                                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#9e9e9e", textTransform: "uppercase", letterSpacing: "0.7px" }}>
                                            Shipment Setup
                                        </Typography>
                                    </CardHead>
                                    <CardBody>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <FieldToggle
                                                    name="order_type"
                                                    label="Order Type *"
                                                    options={[{ label: "Forward Shipment", value: "FORWARD" }, { label: "Reverse Shipment", value: "REVERSE" }]}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <FieldToggle
                                                    name="surface_Air"
                                                    label="Shipment Mode *"
                                                    options={[{ label: "Surface", value: "SURFACE" }, { label: "Air", value: "AIR" }]}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <FieldToggle
                                                    name="select_courier"
                                                    label="Select Courier"
                                                    externalChange={(e) => setAutoallocate(e.target.value)}
                                                    options={[{ label: "Auto Allocate", value: "autoAllocate" }, { label: "None", value: "none" }]}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardBody>
                                </FormCard>

                                {/* Delivery Options */}
                                <FormCard>
                                    <CardHead>
                                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#9e9e9e", textTransform: "uppercase", letterSpacing: "0.7px" }}>
                                            Delivery Options
                                        </Typography>
                                        <Typography sx={{ fontSize: "11px", color: "#bdbdbd", ml: "auto" }}>
                                            All required
                                        </Typography>
                                    </CardHead>
                                    <CardBody>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FieldToggle
                                                    name="appointmentDelivery"
                                                    label="Appointment Required *"
                                                    options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FieldToggle
                                                    name="mallDelivery"
                                                    label="Mall Delivery *"
                                                    options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FieldToggle
                                                    name="csdDelivery"
                                                    label="CSD Delivery *"
                                                    options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FieldToggle
                                                    name="carrierRisk"
                                                    label="Carrier Risk *"
                                                    options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardBody>
                                </FormCard>

                            </Box>
                        )}

                        {/* ── Step 1: Order Info ───────────────────────────── */}
                        {currentStep === 1 && (
                            <FormCard>
                                <CardBody>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}><RHFTextField label="Reference *" name="reference" /></Grid>
                                        <Grid item xs={12} md={6}><RHFTextField label="Invoice Number *" name="invoice_number" /></Grid>
                                        <Grid item xs={12} md={6}><RHFTextField label="Invoice Amount *" type="number" name="invoice_amount" /></Grid>
                                        <Grid item xs={12} md={6}><RHFTextField label="Eway Bill" type="number" name="ewayBill" /></Grid>
                                        <Grid item xs={12} md={6}><RHFTextField InputLabelProps={{ shrink: true }} label="Invoice Date *" type="date" name="invoice_date" /></Grid>
                                        <Grid item xs={12} md={6}><RHFTextField label="Insurance Taken" name="insurance_taken" /></Grid>
                                    </Grid>
                                </CardBody>
                            </FormCard>
                        )}

                        {/* ── Step 2: Pickup Details ───────────────────────── */}
                        {currentStep === 2 && (
                            <FormCard>
                                <CardBody>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}><RHFTextField label="Pickup Warehouse Name *" name="pickup_warehouse_name" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Pickup Person Name *" name="pickup_person_name" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Pickup Person Phone *" name="pickup_person_phone" /></Grid>
                                        <Grid item xs={12}><RHFTextField label="Pickup Reason" name="pickup_reason" /></Grid>
                                    </Grid>
                                </CardBody>
                            </FormCard>
                        )}

                        {/* ── Step 3: Drop Details ─────────────────────────── */}
                        {currentStep === 3 && (
                            <FormCard>
                                <CardBody>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}><RHFTextField label="Drop Address *" name="drop_address" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Drop Consignee *" name="drop_consignee" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Drop Phone *" name="drop_phone" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Drop City *" name="drop_city" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Drop Pincode *" name="drop_pincode" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Drop Region *" name="drop_region" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Drop GST Number" name="drop_gst_number" /></Grid>
                                    </Grid>
                                </CardBody>
                            </FormCard>
                        )}

                        {/* ── Step 4: Payment Details ──────────────────────── */}
                        {currentStep === 4 && (
                            <FormCard>
                                <CardBody>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <RHFRadioGroup label="Payment Mode *" name="mode_of_payment"
                                                onChange={(e) => setPaymentOption(e.target.value)}
                                                options={[{ label: "PREPAID", value: "PREPAID" }, { label: "COD", value: "COD" }]} />
                                        </Grid>
                                        {paymentOption === "COD" && (
                                            <>
                                                <Grid item xs={12} md={6}><RHFTextField label="COD Collection" name="cod_collection" /></Grid>
                                                <Grid item xs={12} md={6}><RHFTextField label="COD Amount" type="number" name="cod_amount" /></Grid>
                                                <Grid item xs={12} md={6}><RHFTextField label="Cheque in Favor of" name="cheque_in_favor_of" /></Grid>
                                                <Grid item xs={12}>
                                                    <RHFRadioGroup label="COD Type" name="codType"
                                                        options={[{ label: "CASH", value: "CASH" }, { label: "CHEQUE", value: "CHEQUE" }, { label: "DRAFT", value: "DRAFT" }]} />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </CardBody>
                            </FormCard>
                        )}

                        {/* ── Step 5: Return Details ───────────────────────── */}
                        {currentStep === 5 && (
                            <FormCard>
                                <CardBody>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}><RHFTextField label="Return Address" name="return_address" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Return Consignee" name="return_consignee" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Return Phone" name="return_phone" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Return City" name="return_city" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Return Pincode" name="return_pincode" /></Grid>
                                        <Grid item xs={12} sm={6}><RHFTextField label="Return Region" name="return_region" /></Grid>
                                    </Grid>
                                </CardBody>
                            </FormCard>
                        )}

                        {/* ── Step 6: Packages ─────────────────────────────── */}
                        {currentStep === 6 && (
                            <FormCard>
                                <CardBody>
                                    <Grid container spacing={2} sx={{ mb: 2.5 }}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <RHFTextField InputLabelProps={{ shrink: true }} label="Order Ready Date *" type="date" name="order_ready_date" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <RHFTextField label="Total Weight (kg) *" type="number" name="package_total_weight" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <RHFTextField label="Total Package Count *" type="number" name="total_package_count" />
                                        </Grid>
                                    </Grid>

                                    <PkgBox>
                                        <PkgHead><PkgBadge>Package 1</PkgBadge></PkgHead>
                                        <Box sx={{ p: 1.5, display: "grid", gridTemplateColumns: { xs: "repeat(2,1fr)", sm: "repeat(3,1fr)", md: "repeat(5,1fr)" }, gap: 1.5 }}>
                                            <RHFTextField label="Units *" type="number" name="package1_no_of_units" size="small" />
                                            <RHFTextField label="Length (cm) *" type="number" name="package1_length" size="small" />
                                            <RHFTextField label="Width (cm) *" type="number" name="package1_width" size="small" />
                                            <RHFTextField label="Height (cm) *" type="number" name="package1_height" size="small" />
                                            <RHFTextField label="Weight (kg) *" type="number" name="package1_weight" size="small" />
                                        </Box>
                                    </PkgBox>

                                    {parcels.map((parcel, index) => (
                                        <PkgBox key={parcel.id}>
                                            <PkgHead>
                                                <PkgBadge>Package {index + 2}</PkgBadge>
                                                <Button size="small" color="error"
                                                    startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => removeParcel(parcel.id)}
                                                    sx={{ ml: "auto", fontSize: "0.75rem", textTransform: "none", fontWeight: 600 }}>
                                                    Remove
                                                </Button>
                                            </PkgHead>
                                            <Box sx={{ p: 1.5, display: "grid", gridTemplateColumns: { xs: "repeat(2,1fr)", sm: "repeat(3,1fr)", md: "repeat(5,1fr)" }, gap: 1.5 }}>
                                                <RHFTextField label="Units" type="number" name={`package${index + 2}_no_of_units`} size="small" />
                                                <RHFTextField label="Length (cm)" type="number" name={`package${index + 2}_length`} size="small" />
                                                <RHFTextField label="Width (cm)" type="number" name={`package${index + 2}_width`} size="small" />
                                                <RHFTextField label="Height (cm)" type="number" name={`package${index + 2}_height`} size="small" />
                                                <RHFTextField label="Weight (kg)" type="number" name={`package${index + 2}_weight`} size="small" />
                                            </Box>
                                        </PkgBox>
                                    ))}

                                    <Box sx={{ mt: 2 }}>
                                        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addParcel}
                                            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: "0.8rem", borderStyle: "dashed" }}>
                                            Add Package
                                        </Button>
                                    </Box>
                                </CardBody>
                            </FormCard>
                        )}

                        {/* ── Step 7: Review & Submit ──────────────────────── */}
                        {isReview && (
                            <ReviewStep values={allValues} parcels={parcels} onEdit={setCurrentStep} />
                        )}

                        {/* ── Navigation ────────────────────────────────────── */}
                        <NavBar>
                            <NavBtn
                                variant="outlined"
                                color="inherit"
                                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                                onClick={handleBack}
                                disabled={currentStep === 0}
                                sx={{ visibility: currentStep === 0 ? "hidden" : "visible" }}
                            >
                                Back
                            </NavBtn>

                            <Box sx={{ display: "flex", gap: 1 }}>
                                {!isReview && (
                                    <NavBtn
                                        variant="contained"
                                        color="primary"
                                        disableElevation
                                        endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                                        onClick={handleNext}
                                    >
                                        Next
                                    </NavBtn>
                                )}
                                {isReview && (
                                    <>
                                        <NavBtn
                                            variant="outlined"
                                            color="inherit"
                                            startIcon={<RestartAltIcon />}
                                            onClick={() => { reset(); setParcels([]); setCurrentStep(0); }}
                                        >
                                            Reset
                                        </NavBtn>
                                        <SubmitBtn
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disableElevation
                                            loading={isSubmitting}
                                            startIcon={<SendOutlinedIcon sx={{ fontSize: 16 }} />}
                                        >
                                            Submit Request
                                        </SubmitBtn>
                                    </>
                                )}
                            </Box>
                        </NavBar>

                    </ContentArea>
                </WizardLayout>
            </FormProvider>
        </>
    );
}
